import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom-v5-compat";
import { ShopFilter } from "@b2bportal/air-price-watch-api";
import { deleteWatch, trackEvent } from "@hopper-b2b/api";
import {
  cartFulfillSelectors,
  ContactSelectors,
  DesktopCheckoutHeader,
  FlightSelectors,
  SeatSelectors,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useExperiment } from "@hopper-b2b/experiments";
import { I18nMarkup, useI18nContext } from "@hopper-b2b/i18n";
import { writeInitialTripsFilter } from "@hopper-b2b/trips";
import {
  BOOKING_CONFIRMATION,
  type FlightSummaryInfo,
  MyTripsFilter,
  SliceStopCountFilter,
  TripCategory,
} from "@hopper-b2b/types";
import {
  ActionLink,
  ButtonWrap,
  FlightConfirmation,
  IconName,
} from "@hopper-b2b/ui";
import { removeTimezone, useDeviceTypes } from "@hopper-b2b/utilities";
import clsx from "clsx";
import dayjs from "dayjs";
import merge from "lodash-es/merge";
import { EXPERIMENTS } from "../../../../App";
import ArrowCurved from "../../../../assets/client/arrow-curved.svg?react";
import Chevron from "../../../../assets/client/chevron-primary.svg?react";
import Close from "../../../../assets/client/close.svg?react";
import GreenCheck from "../../../../assets/client/green-confirmation-check.svg";
import { RedirectLoader } from "../../../../components/Body/RedirectLoader";
import { useBookingSurveyConfirmation } from "../../../../hooks/useBookingSurveyConfirmation";
import { useCheckoutMyTripsNavigation } from "../../../../hooks/useCheckoutMyTripsNavigation";
import {
  getLocalStorageTrackingEvents,
  removeLocalStorageTrackingEvents,
  useTrackCompleteBuyAir,
} from "../../../../hooks/useFlightLocalStorageTracking";
import { isUserTypeCookieUltravioleta } from "../../../../utils/colors";
import { useNubankFeatureFlagsContext } from "../../../../utils/featureFlagsContext";
import { isFlightDomesticToInternational } from "../../../../utils/isFlightDomesticToInternational";
import { openNubankHelp } from "../../../../utils/nubankHandoff";
import { PATH_TRIPS_FLIGHTS } from "../../../../utils/urlPaths";
import {
  getClientAssets,
  getFlightSearchReturnLabel,
  getSelectedTripDetails,
  getTripCategory,
} from "../../selectors";
import { CrossSellConfirmation } from "../CrossSellConfirmation";
import { ItineraryDetailsModal } from "../Review/components/ItineraryDetailsModal";
import "./styles.scss";

export const BookingConfirmationWrapper: FC<
  PropsWithChildren<{ matchesMobile: boolean }>
> = ({ matchesMobile, children }) => {
  const clientAssets = useSelector(getClientAssets);
  const { t } = useI18nContext();

  return (
    <div className="booking-confirmation-container nubank">
      {matchesMobile ? (
        children
      ) : (
        <div className="booking-confirmation-desktop-container">
          <DesktopCheckoutHeader
            clientLogo={clientAssets?.logo}
            title={t("bookingSuccess.title")}
            subtitle={t("bookingSuccess.subtitle")}
          />
          <div className="booking-confirmation-content-container">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export const BookingConfirmation = () => {
  const history = useHistory();
  const { t, brand } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();
  const contactInformation = useSelector(
    ContactSelectors.getContactInformation
  );
  const itinerary = useSelector(FlightSelectors.getFinalizedItinerary);
  const seats = useSelector(SeatSelectors.getFulfilledSeats);
  const isRoundTrip = useSelector(getTripCategory) === TripCategory.ROUND_TRIP;
  const returnLabel = useSelector(getFlightSearchReturnLabel);
  const tripDetails = useSelector(getSelectedTripDetails);
  const hasPurchasedAirCfar = useSelector(
    cartFulfillSelectors.hasFulfilledAirCfarProduct
  );
  const hasPurchasedDisruption = useSelector(
    cartFulfillSelectors.hasFulfilledDisruptionProduct
  );
  const hasPurchasedAirChfar = useSelector(
    cartFulfillSelectors.hasFulfilledAirChfarProduct
  );
  const airports = useSelector(FlightSelectors.getAirportMap);
  const localStorageTrackingEventsKey = useSelector(
    FlightSelectors.getLocalStorageKey
  );
  const trackCompleteBuyAir = useTrackCompleteBuyAir();
  const { enableCsatSurvey } = useNubankFeatureFlagsContext();
  useBookingSurveyConfirmation({
    vertical: "flights",
    bookingId: itinerary?.id,
    enabled: enableCsatSurvey,
  });
  const isUltravioletaUser = isUserTypeCookieUltravioleta();

  const [openItineraryDetails, setOpenItineraryDetails] = useState<
    "departure" | "return" | null
  >(null);

  const airportMap = useMemo(() => {
    if (airports && Object.keys(airports).length > 0) return airports;
    if (!tripDetails?.airports?.length) return airports ?? {};
    return Object.fromEntries(tripDetails.airports.map((a) => [a.code, a]));
  }, [airports, tripDetails?.airports]);

  const getFlightSummaryInfo = useCallback(
    (isOutgoing: boolean): FlightSummaryInfo | null => {
      const slice = tripDetails.slices.find((s) => s.outgoing === isOutgoing);
      if (!slice) return null;
      const originLabel =
        airportMap?.[slice.originCode]?.cityName ??
        slice.originName ??
        slice.originCode;
      const destinationLabel =
        airportMap?.[slice.destinationCode]?.cityName ??
        slice.destinationName ??
        slice.destinationCode;

      return {
        originCity: originLabel,
        destinationCity: destinationLabel,
        arrival: slice.arrivalTime,
        departure: slice.departureTime,
        airlineName: slice.segmentDetails?.[0]?.airlineName,
        airlineCode: slice.segmentDetails?.[0]?.airlineCode,
        isDeparture: slice.outgoing,
        title: t("originToDestination", {
          origin: originLabel,
          destination: destinationLabel,
        }),
        stops: slice.stops,
      };
    },
    [airportMap, t, tripDetails.slices]
  );

  // For removing a Price watch, we need two values from the urlParams
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const filter = urlParams.get(
    "stopsOption"
  ) as unknown as SliceStopCountFilter;

  // noLCC isn't propagating correctly from flight shop, since this is a stop-gap solution until Brooklyn handles it correctly this is okay.
  const priceWatchAlertKeyFilters: ShopFilter[] =
    filter === SliceStopCountFilter.NONE
      ? [ShopFilter.NonStopNoLCC, ShopFilter.NonStop]
      : [ShopFilter.NoLCC, ShopFilter.NoFilter];

  const priceWatchAlertKeyBase = useSelector(
    FlightSelectors.getPriceWatchAlertKeyWithNoFilter
  );

  const isDomesticToInternational = isFlightDomesticToInternational(
    tripDetails,
    brand?.preferredCountryCode
  );

  const enableTravelInsurance = useExperiment(
    EXPERIMENTS.NUBANK_TRAVEL_INSURANCE
  );

  const enableCrossSellConfirmationScreen = useExperiment(
    EXPERIMENTS.CROSS_SELL_CONFIRMATION_SCREEN
  );

  useEffect(() => {
    document.title = t("bookingConfirmation.documentTitle");
    return () => {
      document.title = t("documentTitle");
    };
  }, [t]);

  const bookingStatus = useMemo(
    () =>
      t("bookingConfirmation.status", {
        id: itinerary?.locators?.agent.unscopedValue || "",
      }),
    [itinerary, t]
  );

  useEffect(() => {
    const propertiesFromStorage = getLocalStorageTrackingEvents(
      localStorageTrackingEventsKey
    );
    trackCompleteBuyAir(propertiesFromStorage);
    removeLocalStorageTrackingEvents(localStorageTrackingEventsKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackEvent({ eventName: BOOKING_CONFIRMATION, properties: undefined });
    // Attempt to delete watch
    if (priceWatchAlertKeyBase) {
      priceWatchAlertKeyFilters.forEach((filter: ShopFilter) => {
        deleteWatch({
          key: merge(priceWatchAlertKeyBase, { value: { filter: filter } }),
        });
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectToLegacyTrips = useCallback(() => {
    writeInitialTripsFilter(MyTripsFilter.FLIGHTS);
    history.push(PATH_TRIPS_FLIGHTS);
  }, [history]);
  const { navigateToMyTrips: redirectToTrips, nativeHandoffPending } =
    useCheckoutMyTripsNavigation(redirectToLegacyTrips);

  const handleClose = redirectToTrips;

  const openFlightDetailsModal = (type: "departure" | "return" | null) =>
    setOpenItineraryDetails(type);

  const getCustomDate = useCallback((date: string) => {
    return (
      <>
        {dayjs(removeTimezone(date)).format("dddd")},{" "}
        <span className="uppercase">
          {dayjs(removeTimezone(date)).format("DD MMM YY")}
        </span>
      </>
    );
  }, []);

  const getCustomTime = useCallback((departure: string, arrival: string) => {
    return `${dayjs(removeTimezone(departure)).format("HH")}h${dayjs(
      removeTimezone(departure)
    ).format("m")} - ${dayjs(removeTimezone(arrival)).format("HH")}h${dayjs(
      removeTimezone(arrival)
    ).format("m")}`;
  }, []);

  const goToFaqs = useCallback(() => {
    openNubankHelp();
  }, []);

  const outgoingInfo = useMemo(
    () => getFlightSummaryInfo(true),
    [getFlightSummaryInfo]
  );

  return (
    <BookingConfirmationWrapper matchesMobile={matchesMobile}>
      {nativeHandoffPending ? <RedirectLoader /> : null}
      <div
        className={clsx("nubank-flight-booking-success-container", {
          mobile: matchesMobile,
          oneWay: !isRoundTrip,
          "cross-sell": enableCrossSellConfirmationScreen,
        })}
      >
        <div className="flight-booking-confirmation-top">
          <div className="flight-booking-success-header">
            <ActionLink
              onClick={handleClose}
              content={<Close className="close-button-icon" />}
            />
            <div className="flight-booking-success-header-title">
              {t("bookingConfirmation.documentTitle")}
            </div>
          </div>
          {enableCrossSellConfirmationScreen ? (
            <CrossSellConfirmation outgoingInfo={outgoingInfo} />
          ) : (
            <FlightConfirmation
              statusLabel={bookingStatus}
              title={t("bookingConfirmation.title")}
              subtitle={
                <I18nMarkup
                  tKey={"bookingConfirmation.subtitle"}
                  replacements={{
                    email: contactInformation.email,
                  }}
                />
              }
              nextHeader={t("bookingConfirmation.whatsNext")}
              outgoingFlightSummaryInfo={getFlightSummaryInfo(true)}
              returningFlightSummaryInfo={
                isRoundTrip && returnLabel
                  ? getFlightSummaryInfo(false)
                  : undefined
              }
              isMobile={matchesMobile}
              seats={seats?.seats}
              hasPurchasedAirCfar={hasPurchasedAirCfar}
              hasPurchasedDisruption={hasPurchasedDisruption}
              hasPurchasedAirChfar={hasPurchasedAirChfar}
              headerImage={GreenCheck}
              expandIcon={IconName.ChevronDown}
              openFlightDetailsModal={openFlightDetailsModal}
              getCustomDate={getCustomDate}
              getCustomTime={getCustomTime}
              showMoreInformation={
                isUltravioletaUser &&
                enableTravelInsurance &&
                isDomesticToInternational
              }
            />
          )}
        </div>
        <div className="flight-booking-confirmation-bottom">
          <div className="confirmation-screen-cancellation-disclaimer">
            <ArrowCurved className="confirmation-screen-cancellation-disclaimer-icon" />
            <p className="confirmation-screen-cancellation-disclaimer-content">
              {t("flightConfirmationCancellationDisclosure")}{" "}
              <ButtonWrap
                className="confirmation-screen-cancellation-disclaimer-link"
                onClick={goToFaqs}
              >
                {t("learnMore")}
                <Chevron />
              </ButtonWrap>
            </p>
          </div>
          <button className="to-trips-button" onClick={redirectToTrips}>
            {t("myTrips")}
          </button>
        </div>
      </div>
      <ItineraryDetailsModal
        openModal={openItineraryDetails}
        onClose={() => setOpenItineraryDetails(null)}
      />
    </BookingConfirmationWrapper>
  );
};
